import { ChannelAccessChangedEvent, WebSocketService } from './gateway.service';
import { Injectable } from '@angular/core';
import { IChat, IChatItem, IColumnCard, IResponseWithData } from '../interfaces';
import { environment } from '../../environments/environment';
import { catchError, combineLatest, map, Observable, of, shareReplay, Subject, Subscription, tap } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LEFT_MENU, MenuItem, RIGHT_MENU } from '../components/layout/chat-sidebar/chat-sidebar.interface';
import {
  CHAT_CONVERSATION_TYPE,
  CHAT_FILTER_NONE,
  CHAT_SORT,
  CHAT_TABS,
  IChatsInfo,
  IInitChatsParams,
} from '../components/layout/chat-sidebar/components/chat-list/chat-list.interface';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import {
  ADVERT_TYPE,
  CHAT_EXTERNAL_TYPE,
  DOWNLOAD_MEDIA_TYPE,
  EXTERNAL_ID_PREFIX,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  MessageSocketAction,
  ACTIVE_EMPLOYEES_PARAMS,
} from '../enum/constants';
import {
  IChatAdditionalInfo,
  IChatMessagesInfo,
  IChatSearchInfo,
  IInitMessagesParams,
  IRiaAdvert,
  ISendMessageBody,
  IUserInfo,
  MESSAGES_INFO_POSITION,
  OLD_RIA_CHAT_TYPES,
  RIA_CHAT_TYPES,
  ScrollParams,
  TGroupItem,
  TMessageUpdatedSocket,
} from '../components/layout/chat-sidebar/components/chat/chat.interface';
import {
  Message,
  MessageTranscription,
  noEditAttachmentTypes,
  TranslateMessageResponse,
} from '../components/layout/chat-sidebar/components/chat-message/chat-message.interface';
import { BaseService } from './base.service';
import { IBlockedUser } from '../components/layout/chat-sidebar/components/chat-settings/chat-settings.interface';
import { IAboutAction, TOOLS } from '../components/layout/chat-sidebar/components/chat-about/chat-about.interface';
import { CreateChatForMessagePayload } from '../modals/write-message/write-message.interface';
import { formatFullName } from '../helpers';

export type ChatManager = { id: string; name: string; avatar_url?: string | null };

type ChatUnreadState = {
  id: string;
  unread_count?: number | null;
  is_mark_unread?: boolean | null;
};

// The only channels where a chat can hold post comments instead of a direct dialog
const COMMENT_CHAT_EXTERNAL_TYPES: string[] = [
  CHAT_EXTERNAL_TYPE.INSTAGRAM,
  CHAT_EXTERNAL_TYPE.FACEBOOK,
  CHAT_EXTERNAL_TYPE.TIKTOK,
];

const COMMENT_EXTERNAL_ID_PREFIXES: string[] = [
  EXTERNAL_ID_PREFIX.INSTAGRAM_COMMENT,
  EXTERNAL_ID_PREFIX.FACEBOOK_COMMENT,
  EXTERNAL_ID_PREFIX.TIKTOK_COMMENT,
];

const isCommentExternalId = (externalId?: string): boolean =>
  COMMENT_EXTERNAL_ID_PREFIXES.some((prefix) => !!externalId?.startsWith(prefix));

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  public message_count = 0;
  private readonly chatUnreadStateChangeSubject = new Subject<ChatUnreadState>();
  public readonly chatUnreadStateChange$ = this.chatUnreadStateChangeSubject.asObservable();
  public readonly menuItems: MenuItem[] = [
    { is_mat_icon: true, icon: 'chat', key: 'external' },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/olx_off_color.svg',
      key: 'olx',
      external_type: CHAT_EXTERNAL_TYPE.OLX,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/autoria_off_color.png',
      key: 'autoria',
      external_type: CHAT_EXTERNAL_TYPE.AUTO_RIA,
      hidden: !this.baseService.company.settings?.is_ria,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/autoria_old_off_color.png',
      key: 'autoria_old',
      external_type: CHAT_EXTERNAL_TYPE.AUTO_RIA_WH,
      hidden: !['dev', 'prod-test', 'bossauto'].includes(this.baseService.company.subdomain), // Temp
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/telegram.svg',
      key: 'telegram_user',
      external_type: CHAT_EXTERNAL_TYPE.TELEGRAM_USER,
      hidden: !this.baseService.company.settings?.is_telegram_user,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/telegram_bot.png',
      key: 'telegram_bot',
      external_type: CHAT_EXTERNAL_TYPE.TELEGRAM_BOT,
      hidden: !this.baseService.company.settings?.is_telegram_bot,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/widget.jpg',
      key: 'chat_widget',
      external_type: CHAT_EXTERNAL_TYPE.CHAT_WIDGET,
      hidden: !this.baseService.company.settings?.is_widget,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/instagram.svg',
      key: 'instagram',
      external_type: CHAT_EXTERNAL_TYPE.INSTAGRAM,
      hidden: !this.baseService.company.settings?.is_instagram,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/facebook.svg',
      key: 'facebook',
      external_type: CHAT_EXTERNAL_TYPE.FACEBOOK,
      hidden: !this.baseService.company.settings?.is_facebook,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/viber.svg',
      key: 'viber',
      external_type: CHAT_EXTERNAL_TYPE.VIBER,
      hidden: !this.baseService.company.settings?.is_viber,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/whatsapp.svg',
      key: 'whatsapp',
      external_type: CHAT_EXTERNAL_TYPE.WHATSAPP,
      hidden: !this.baseService.company.settings?.is_whatsapp,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/prom.svg',
      key: 'prom',
      external_type: CHAT_EXTERNAL_TYPE.PROM,
      hidden: !this.baseService.company.settings?.is_prom,
    },
    {
      is_mat_icon: false,
      icon: '/assets/svgs/integrations/tiktok.svg',
      key: 'tiktok',
      external_type: CHAT_EXTERNAL_TYPE.TIKTOK,
      hidden: !this.baseService.company.settings?.is_tiktok,
    },
  ];
  public selectedTab!: MenuItem;
  public chatsInfo: IChatsInfo = {
    items: [],
    page_number: 1,
    page_count: 1,
    loaded_pages: [],
    search_text: '',
  };
  public chatMessagesInfo: IChatMessagesInfo = {
    items: [],
    grouped_items: [],
  };
  public selectedChatId: string = '';
  public chat!: IChat;
  public advert!: any;
  public rightMenu!: string | null;
  public leftMenu!: string | null;
  public deals!: IColumnCard[] | null;
  public chatIdQuery: string = this.activatedRoute.snapshot.queryParams['chat'];
  public isLoaderChat = {
    up: false,
    down: false,
    center: false,
    messages: false,
    input_actions: false,
  };
  public isLoaderAbout = false;
  public isLoaderChatList = {
    up: false,
    down: false,
    center: false,
  };
  public chatPinnedInfo: IChatAdditionalInfo = {
    items: [],
    grouped_items: [],
    visible: false,
  };
  public chatDelayedInfo: IChatAdditionalInfo = {
    items: [],
    grouped_items: [],
    visible: false,
  };
  public selectedChatTab: CHAT_TABS = CHAT_TABS.ACTIVE;
  public selectedChatSort: CHAT_SORT = CHAT_SORT.IS_NEW;
  public selectedConversationType: CHAT_CONVERSATION_TYPE = CHAT_CONVERSATION_TYPE.ALL;
  // Also drops the dialogs of the opposite type: chats without comments / comments without chats
  public isConversationTypeExclusive = false;
  public selectedChannels: string[] = [];
  public selectedTags: string[] = [];
  public selectedManagers: string[] = [];
  private managers$?: Observable<ChatManager[]>;
  public chatSearchInfo: IChatSearchInfo = {
    loading: false,
    items: null,
    search_text: '',
    search_visible: false,
    page_number: 1,
    page_count: 1,
  };
  private selectChatSubscription!: Subscription;
  public savedPositionData: any = {};
  public userInfo!: IUserInfo | null;
  public blockedUsers: IBlockedUser[] = [];
  private reloadChatListSubscription?: Subscription;
  private accessCheckSubscription?: Subscription;
  public aboutActions: IAboutAction[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly baseService: BaseService,
    private readonly gateway: WebSocketService,
  ) {
    this.loadCount();
    this.gateway.channelAccessChanged().subscribe((event) => this.refreshChannelAccess(event));
  }

  private refreshChannelAccess(event?: ChannelAccessChangedEvent): void {
    this.chatsInfo.items_request?.unsubscribe();
    this.reloadChatListSubscription?.unsubscribe();
    this.managers$ = undefined;
    // Keep the current list visible until the refreshed pages arrive.
    this.isLoaderChatList = { up: false, down: false, center: false };
    this.loadCount();
    if (this.selectedTab) {
      if (this.chatsInfo.loaded_pages.length) this.reloadChatList();
      else this.initChats(this.chatsInfo.items.length === 0);
    }

    const chatId = this.selectedChatId;
    if (!chatId) return;
    const channels = this.chat?.id === chatId ? this.chat.channels : [];
    const affectedChannelIds = event?.channel_ids;
    if (affectedChannelIds && channels?.length && !channels.some((channel) => affectedChannelIds.includes(channel.id)))
      return;

    this.accessCheckSubscription?.unsubscribe();
    this.accessCheckSubscription = this.getChatById(chatId).subscribe({
      // Rechecking access must not replace the open chat or restart its message requests.
      error: (error: HttpErrorResponse) => {
        if (this.selectedChatId !== chatId || !this.isChatAccessDenied(error)) return;
        this.selectChatSubscription?.unsubscribe();
        this.chatMessagesInfo.items_request?.unsubscribe();
        this.resetChat();
        this.closeChat();
        this.isLoaderChat.center = false;
        this.isLoaderAbout = false;
      },
    });
  }

  private isChatAccessDenied(error: HttpErrorResponse): boolean {
    return [403, 404].includes(error.status) || error.error?.errors?.some(
      (item: { code: string }) => ['ACCESS_REJECTED', 'CHAT_NOT_FOUND'].includes(item.code),
    ) === true;
  }

  public initChatIdQuery(chatId?: string): void {
    const chatIdQuery = chatId ?? this.activatedRoute.snapshot.queryParams['chat'];
    this.router.navigate([], {
      queryParams: {
        'chat': chatIdQuery || '',
      },
      queryParamsHandling: 'merge',
    });
    if (chatIdQuery || chatIdQuery === '') this.chatIdQuery = chatIdQuery;
  }

  public setChatIdQuery() {
    this.router.navigate([], {
      queryParams: {
        'chat': this.chat.id,
      },
      queryParamsHandling: 'merge',
    });
    this.chatIdQuery = this.chat.id;
  }

  public resetChatIdQuery(): void {
    this.router.navigate([], {
      queryParams: {
        'chat': null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public selectChat(chatId: string, messageId?: string): void {
    this.accessCheckSubscription?.unsubscribe();
    this.resetChat();
    this.isLoaderChat.center = true;
    this.isLoaderAbout = true;
    this.selectedChatId = chatId;

    if (this.selectChatSubscription) {
      this.selectChatSubscription.unsubscribe();
    }
    this.selectChatSubscription = this.getChatById(chatId)
      .subscribe({
        next: (res: IChat) => {
          this.isLoaderChat.center = false;
          if (!res) {
            this.isLoaderAbout = false;
            return;
          }

          this.chat = res;
          this.setChatIdQuery();

          if (OLD_RIA_CHAT_TYPES.includes(res.external_type)) {
            this.generateRiaChat();
          } else {
            this.loadChatMessages(true, { message_id: messageId });
          }

          this.getPinnedByChat();
          this.getDelayedByChat();

          this.getAdditionalInfo();

          if (this.baseService.currentUserInfo.settings.is_message_read && this.chat.is_can_read) {
            this.readChat(this.chat).subscribe();
          }
        },
        error: () => {
          this.isLoaderAbout = false;
          this.isLoaderChat.center = false;
        },
      });
  }

  private generateRiaLink() {
    if (!this.chat.x_data?.adv_link || this.chat.x_data?.chat_id) return;

    const urlSearchParams = new URLSearchParams(this.chat.x_data.adv_link);
    const url = new URL(this.chat.x_data.adv_link);
    return `https://chat.ria.com/0/0/${this.chat.x_data.chat_id}/?key=${urlSearchParams.get('chat_key')}&amp;chat_on_site=${url.host}&amp;a`;
  }

  private generateRiaChat() {
    this.isLoaderChat.messages = true;
    const ifrmBox = document.createElement('div');
    ifrmBox.classList.add('iframe-box');
    const ifrm = document.createElement('iframe');
    const riaLink = this.generateRiaLink();
    if (!riaLink) {
      this.isLoaderChat.messages = true;
      return;
    }
    ifrm.setAttribute('src', riaLink);
    ifrm.style.width = '335px';
    ifrm.style.height = '568px';
    ifrmBox.appendChild(ifrm);
    ifrm.addEventListener('load', () => {
      ifrmBox.style.display = 'flex';
      this.isLoaderChat.messages = false;
    });
    const observer = new MutationObserver(() => {
      const div = document.getElementById('chat-box');
      if (div) {
        div.appendChild(ifrmBox);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  public destroyRiaChat() {
    if (!OLD_RIA_CHAT_TYPES.includes(this.chat?.external_type)) return;

    this.isLoaderChat.messages = false;
    const div = document.getElementById('chat-box');
    const iframe = document.getElementById('iframe-box');
    if (div && iframe) {
      div.removeChild(iframe);
    }
  }

  public findMessage(message_id: string, scroll_params?: ScrollParams) {
    const messageElement = document.getElementById(message_id);

    if (!messageElement) {
      this.loadChatMessages(true, { message_id, scroll_params });
    } else {
      this.scrollToMessage(messageElement, scroll_params);
    }

    const candidatePage = this.chatSearchInfo.page_number + 1;
    if (this.chatSearchInfo.items?.at(-1)?.id === message_id
      && this.chatSearchInfo.page_number !== this.chatSearchInfo.page_count
      && !this.chatSearchInfo.loading) {
      this.chatSearchInfo.page_number = candidatePage;
      this.searchChatMessages(true);
    }
  }

  public scrollToMessage(messageElement: HTMLElement, scrollParams: ScrollParams = {}) {
    const { behavior, is_highlighted = true } = scrollParams;

    messageElement.scrollIntoView({ behavior, block: 'center' });

    if (is_highlighted) {
      if (messageElement.classList.contains('chat-message-highlighted')) {
        messageElement.classList.remove('chat-message-highlighted');
        void messageElement.offsetWidth;
      }

      messageElement.classList.add('chat-message-highlighted');
      const handleAnimationEnd = () => {
        messageElement.classList.remove('chat-message-highlighted');
        messageElement.removeEventListener('animationend', handleAnimationEnd);
      };
      messageElement.addEventListener('animationend', handleAnimationEnd);
    }
  }


  public groupMessagesByDate(messages: Message[]): TGroupItem[] {
    const grouped = messages.reduce<Record<string, Message[]>>((acc, message) => {
      const date = moment(message.created_at).format('YYYY-MM-DD');

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].unshift(message);
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([date, messages]): TGroupItem => ({
        date,
        messages,
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  public resetChat(): void {
    this.destroyRiaChat();
    this.chat = {} as IChat;
    this.resetChatMessagesInfo();
    this.chatSearchInfo.search_visible = false;
    this.resetChatSearchInfo();
    this.resetChatPinnedInfo();
    this.resetChatDelayedInfo();
    this.advert = null;
    this.deals = null;
    this.userInfo = null;
    this.isLoaderChat.input_actions = false;
  }

  public resetChatSearchInfo(): void {
    if (this.chatSearchInfo.items_request) {
      this.chatSearchInfo.items_request.unsubscribe();
    }
    this.chatSearchInfo.loading = false;
    this.chatSearchInfo.search_text = '';
    this.chatSearchInfo.items = null;
    this.chatSearchInfo.page_number = 1;
    this.chatSearchInfo.page_count = 1;
    this.chatSearchInfo.selected_number = undefined;
  }

  public resetChatPinnedInfo(): void {
    this.chatPinnedInfo.visible = false;
    this.chatPinnedInfo.items = [];
    this.chatPinnedInfo.grouped_items = [];
  }

  public resetChatDelayedInfo(): void {
    this.chatDelayedInfo.visible = false;
    this.chatDelayedInfo.items = [];
    this.chatDelayedInfo.grouped_items = [];
  }

  public resetChatMessagesInfo(): void {
    this.chatMessagesInfo.items = [];
    this.chatMessagesInfo.grouped_items = [];
    this.chatMessagesInfo.total_first = undefined;
    this.chatMessagesInfo.total_last = undefined;
  }

  public getChats(params: any, body: any = {}) {
    return this.http.post<any>(environment.baseURL + '/chats', body, { params })
      .pipe(map(res => res.data));
  }

  public getChatById(chat_id: string) {
    return this.http.get<any>(environment.baseURL + `/chats/${chat_id}`)
      .pipe(map(res => res.data));
  }

  public getChatMessage(chat_id: string, params: any) {
    return this.http.post<any>(environment.baseURL + `/chats/${chat_id}/messages`, {}, { params })
      .pipe(map(res => res.data));
  }

  public resetChatsInfo(): void {
    this.chatsInfo.items = [];
    this.chatsInfo.page_number = 1;
    this.chatsInfo.page_count = 1;
    this.chatsInfo.loaded_pages = [];
    this.chatsInfo.total = undefined;
  }

  public resetAndInitChatList() {
    this.resetChatsInfo();
    this.initChats();
  }

  // Comments exist only in Instagram, Facebook and TikTok, other tabs have nothing to split
  public isConversationTypeAvailable(): boolean {
    const externalType = this.selectedTab?.external_type;
    if (externalType) return COMMENT_CHAT_EXTERNAL_TYPES.includes(externalType);

    const settings = this.baseService.company.settings;
    return !!(settings?.is_instagram || settings?.is_facebook || settings?.is_tiktok);
  }

  // The selection is kept across tabs but applied only where the filter is available
  public get appliedConversationType(): CHAT_CONVERSATION_TYPE {
    return this.isConversationTypeAvailable() ? this.selectedConversationType : CHAT_CONVERSATION_TYPE.ALL;
  }

  public get appliedConversationExclusive(): boolean {
    return this.appliedConversationType !== CHAT_CONVERSATION_TYPE.ALL && this.isConversationTypeExclusive;
  }

  private getChatListParams(page: number): Record<string, any> {
    const params: Record<string, any> = {
      page,
      take: 25,
      sort: this.selectedChatSort,
    };

    if (this.selectedTab.external_type) {
      params['external_type'] = this.selectedTab.external_type;
    }
    if (this.selectedChatTab) {
      params['type'] = this.selectedChatTab;
    }
    if (this.appliedConversationType !== CHAT_CONVERSATION_TYPE.ALL) {
      params['conversation_type'] = this.appliedConversationType;
      if (this.appliedConversationExclusive) {
        params['conversation_exclusive'] = true;
      }
    }
    if (this.selectedChannels.length) {
      params['channels'] = this.selectedChannels;
    }
    if (this.selectedTags.length) {
      params['tags'] = this.selectedTags;
    }
    if (this.selectedManagers.length) {
      params['managers'] = this.selectedManagers;
    }
    if (this.chatsInfo.search_text) {
      params['search'] = this.chatsInfo.search_text;
    }

    return params;
  }

  public getAvailableTypes() {
    return this.http.get<any>(environment.baseURL + '/chats/available-types')
      .pipe(map(res => res.data));
  }

  public initAvailableTypes(): void {
    this.getAvailableTypes().subscribe({
      next: (types: CHAT_EXTERNAL_TYPE[]) => this.hideUnavailableMenuItems(types || []),
      error: () => null,
    });
  }

  private hideUnavailableMenuItems(types: CHAT_EXTERNAL_TYPE[]): void {
    const availableTypes = new Set<string>(types);

    this.menuItems.forEach((item) => {
      if (!item.external_type) return;
      item.hidden = item.hidden || !availableTypes.has(item.external_type);
    });

    if (!this.selectedTab?.hidden) return;

    this.selectedTab = this.menuItems[0];
    localStorage.setItem('selectedTabKey', this.selectedTab.key);
    this.resetAndInitChatList();
  }

  public getDealByChat() {
    return this.http.get<any>(environment.baseURL + `/chats/${this.selectedChatId}/deals`)
      .pipe(map(res => res.data), tap(data => {
        this.deals = data?.length ? [...data] : null;
      }));
  }

  public createLeadCard(chatId: string) {
    return this.http.post<any>(environment.baseURL + `/chats/${chatId}/lead-card`, {})
      .pipe(map(res => res.data));
  }

  private getAdditionalInfo() {
    combineLatest([
      this.getDealByChat(),
      this.initAdvert(),
      this.getUserInfo(),
    ]).subscribe({
      next: () => {
        this.isLoaderAbout = false;
      },
      error: () => {
        this.isLoaderAbout = false;
      },
    });
  }

  public reloadChatList() {
    if (this.reloadChatListSubscription) {
      this.reloadChatListSubscription.unsubscribe();
    }

    const chats: IChatItem[] = [];
    const chatsRequest = [];
    for (const page of this.chatsInfo.loaded_pages) {
      chatsRequest.push(this.getChats(this.getChatListParams(page)));
    }

    this.reloadChatListSubscription = combineLatest(chatsRequest)
      .subscribe({
        next: (response: any[]) => {
          for (const chat of response) {
            chats.push(...chat.items);
          }
          this.chatsInfo.items = chats;
          if (response.length) {
            this.chatsInfo.total = response[0].total;
            this.chatsInfo.page_count = Math.ceil(response[0].total / response[0].size);
          }
        },
        error: () => {
        },
      });
  }

  public reloadMessage(chat_id: string, message_id: string) {
    return this.http.get<any>(environment.baseURL + `/chats/${chat_id}/${message_id}/reload`)
      .pipe(map(res => res.data));
  }

  public updateChat(chat_id: string, payload: any) {
    return this.http.put<any>(environment.baseURL + `/chats/${chat_id}`, payload)
      .pipe(map(res => res.data), tap((data: IChat) => {
        if (this.chat?.id && data?.id && this.chat.id === data.id) {
          Object.assign(this.chat, data);
        }

        if (data && ('unread_count' in data || 'is_mark_unread' in data)) {
          this.emitChatUnreadState(data);
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'assigned_user_id')) this.refreshChannelAccess();
        else this.reloadChatList();
      }));
  }

  public getAssignableManagers(chatId: string): Observable<ChatManager[]> {
    return this.http.get<any>(environment.baseURL + `/chats/${chatId}/managers`).pipe(
      map((res) => (res.data || []).map((manager: any) => ({
        id: String(manager.id),
        name: formatFullName(manager.first_name, manager.last_name) || manager.email || '-',
        avatar_url: manager.avatar_url,
      }))),
      catchError(() => of([])),
    );
  }

  public getManagers(): Observable<ChatManager[]> {
    this.managers$ = this.http
      .get<any>(environment.baseURL + '/employees', { params: { take: 500, page: 1, ...ACTIVE_EMPLOYEES_PARAMS } })
      .pipe(
        map((res) => (Array.isArray(res?.data) ? res.data : res?.data?.items || [])),
        map((managers: any[]) =>
          managers.map((manager) => ({
            id: String(manager.id),
            name: formatFullName(manager.first_name, manager.last_name) || manager.email || '-',
            avatar_url: manager.avatar_url,
          })),
        ),
        catchError(() => {
          this.managers$ = undefined;
          return of([]);
        }),
        shareReplay(1),
      );
    return this.managers$;
  }

  public updateChatTags(chatId: string, tagIds: string[]): Observable<IChat> {
    return this.http.put<IResponseWithData<IChat>>(
      environment.baseURL + `/chats/${chatId}/tags`,
      { tag_ids: tagIds },
    ).pipe(
      map(response => response.data),
      tap(chat => this.syncChatTags(chat)),
    );
  }

  private syncChatTags(chat: IChat): void {
    const tags = chat.tags || [];
    const chatListItem = this.chatsInfo.items.find(item => item.id === chat.id);

    if (chatListItem) {
      chatListItem.tags = tags;
    }

    if (this.chat?.id === chat.id) {
      this.chat.tags = tags;
    }
  }

  public deleteChat(chat_id: string) {
    return this.http.delete<any>(environment.baseURL + `/admin/chats/${chat_id}`)
      .pipe(tap(() => {
        if (this.chat?.id === chat_id) {
          this.chat = null!;
          this.selectedChatId = null!;
        }

        this.reloadChatList();
      }));
  }

  public initChats(isLoader: boolean = true, payload: IInitChatsParams = {}, pagination?: boolean) {
    const {
      direction,
      event_scroll,
    } = payload;

    if (this.isLoaderChatList.down || this.isLoaderChatList.up) return;
    if (isLoader) {
      if (!pagination) {
        this.isLoaderChatList.center = true;
      } else {
        if (payload.direction) {
          this.isLoaderChatList[direction || 'down'] = true;
        }
      }
    }

    const params = this.getChatListParams(this.chatsInfo.page_number);

    if (this.chatsInfo.items_request) {
      this.chatsInfo.items_request.unsubscribe();
    }
    this.chatsInfo.items_request = this.getChats(params)
      .subscribe({
        next: (response: { items: any[], total: number, size: number, page: number }) => {
          this.chatsInfo.loaded_pages.push(this.chatsInfo.page_number);
          this.chatsInfo.loaded_pages = [...new Set(this.chatsInfo.loaded_pages)];
          this.chatsInfo.loaded_pages = this.chatsInfo.loaded_pages.sort((a, b) => a - b);
          if (pagination) {
            let position = 0;
            if (event_scroll) {
              position = event_scroll.target.scrollTop + 1;
            }
            if (direction === 'down') {
              this.chatsInfo.items = [...this.chatsInfo.items, ...response.items];
              if (this.chatsInfo.items.length > 75) {
                this.chatsInfo.items = this.chatsInfo.items.slice(25);
                this.chatsInfo.loaded_pages.shift();
              }
            } else if (direction === 'up') {
              this.chatsInfo.items = [...response.items, ...this.chatsInfo.items];
              if (this.chatsInfo.items.length > 75) {
                this.chatsInfo.items = this.chatsInfo.items.slice(0, 74);
                this.chatsInfo.loaded_pages.pop();
                if (position && event_scroll) {
                  event_scroll.target.scrollTop = position;
                }
              }
            }
          } else {
            this.chatsInfo.items = (response.items || []);
          }

          this.chatsInfo.total = response.total;
          this.chatsInfo.page_count = Math.ceil(
            response.total / response.size,
          );
        },
        error: () => {
          this.isLoaderChatList.center = false;
          this.isLoaderChatList.up = false;
          this.isLoaderChatList.down = false;
        },
        complete: () => {
          this.isLoaderChatList.center = false;
          this.isLoaderChatList.up = false;
          this.isLoaderChatList.down = false;
          if (this.chatIdQuery && !this.chat?.id) {
            this.selectChat(this.chatIdQuery);
          }
        },
      });
  }

  public loadChatMessages(isLoader: boolean = true, payload: IInitMessagesParams = {}, pagination?: boolean) {
    const {
      direction,
      event,
    } = payload;

    if (this.isLoaderChat.down || this.isLoaderChat.up) return;
    if (isLoader) {
      if (!pagination) {
        this.isLoaderChat.messages = true;
      } else if (payload.direction) {
        this.isLoaderChat[direction || 'up'] = true;
      }
    }

    const params: Record<string, any> = {
      page: 0,
      take: 50,
    };

    if (payload.direction === 'down') {
      params['last_message_id'] = this.chatMessagesInfo.items[this.chatMessagesInfo.items.length - 1].id;
    }
    if (payload.direction === 'up') {
      params['first_message_id'] = this.chatMessagesInfo.items[0].id;
    }

    if (payload.message_id) {
      params['message_id'] = payload.message_id;
    }

    if (this.chatMessagesInfo.items_request) {
      this.chatMessagesInfo.items_request.unsubscribe();
    }
    this.chatMessagesInfo.items_request = this.getChatMessage(this.chat.id, params)
      .subscribe({
        next: (response) => {
          if (pagination) {
            if (direction === 'up') {
              this.chatMessagesInfo.items = [...response.items, ...this.chatMessagesInfo.items];
              if (this.chatMessagesInfo.items.length > 100) {
                this.chatMessagesInfo.items = this.chatMessagesInfo.items.slice(0, 99);
              }
            } else if (direction === 'down') {
              this.chatMessagesInfo.items = [...this.chatMessagesInfo.items, ...response.items];
              if (this.chatMessagesInfo.items.length > 100) {
                this.chatMessagesInfo.items = this.chatMessagesInfo.items.slice(50);
              }
            }
          } else {
            this.chatMessagesInfo.items = (response.items || []);
          }

          this.chatMessagesInfo.total_first = response.total_first;
          this.chatMessagesInfo.total_last = response.total_last;
          if (event) {
            this.savePositionAndElement(event);
          }
          this.chatMessagesInfo.grouped_items = this.groupMessagesByDate(this.chatMessagesInfo.items);

          setTimeout(() => {
            if (event) {
              this.resetIfLoadedScroll(event);
            }
          });

          requestAnimationFrame(() => {
            if (payload.message_id) {
              this.findMessage(payload.message_id, payload.scroll_params);
            }
          });
        },
        error: () => {
          this.isLoaderChat.messages = false;
          this.isLoaderChat.up = false;
          this.isLoaderChat.down = false;
        },
        complete: () => {
          this.isLoaderChat.messages = false;
          this.isLoaderChat.up = false;
          this.isLoaderChat.down = false;
        },
      });
  }

  public readChat(chat: IChatItem | IChat) {
    if (!chat.is_mark_unread && !chat.unread_count) return of(null);
    return this.http.post<any>(environment.baseURL + `/chats/${chat.id}/read`, {})
      .pipe(map(res => res.data), tap(data => {
        if (this.chat?.id && data?.id && this.chat.id === data.id) {
          Object.assign(this.chat, data);
        }

        this.emitChatUnreadState({
          id: chat.id,
          unread_count: data?.unread_count ?? 0,
          is_mark_unread: data?.is_mark_unread ?? false,
        });
        this.reloadChatList();
      }));
  }

  private emitChatUnreadState(chat: ChatUnreadState): void {
    if (!chat?.id) return;

    const unreadCount = Number(chat.unread_count ?? 0);
    this.chatUnreadStateChangeSubject.next({
      id: chat.id,
      unread_count: Number.isFinite(unreadCount) ? Math.max(0, unreadCount) : 0,
      is_mark_unread: !!chat.is_mark_unread,
    });
  }

  public getUsersBlocked() {
    this.http.get<any>(environment.baseURL + '/user-blocked')
      .pipe(map(res => res.data))
      .subscribe(data => {
        this.blockedUsers = data;
      });
  }

  public blockUser(user_id: string, type: string, name?: string) {
    const body = { user_id, type: type, ...(name ? { name } : {}) };

    return this.http.post<any>(environment.baseURL + '/user-blocked/block', body)
      .pipe(map(res => res.data), tap(() => {
        const currentUserId = String(this.chat?.x_data?.interlocutor_id || this.chat?.x_data?.opponent?.id || '');
        console.log(currentUserId, user_id, currentUserId === user_id);
        if (currentUserId && user_id && currentUserId === user_id) {
          Object.assign(this.chat, {
            is_blocked: true,
          });
        }

        this.reloadChatList();
        if (this.blockedUsers.length) this.getUsersBlocked();
      }));
  }

  public unblockUser(user_id: string, type: string) {
    this.http.post<any>(environment.baseURL + '/user-blocked/unblock', { user_id, type })
      .subscribe(() => {
        const currentUserId = String(this.chat?.x_data?.interlocutor_id || this.chat?.x_data?.opponent?.id || '');
        if (currentUserId && user_id && currentUserId === user_id) {
          Object.assign(this.chat, {
            is_blocked: false,
          });
        }

        this.reloadChatList();
        if (this.blockedUsers.length) this.getUsersBlocked();
      });
  }

  public getAdvert(chat_id: string) {
    const url = environment.baseURL + `/chats/${chat_id}/advert`;
    return this.http.get<any>(url)
      .pipe(map(res => res.data), tap(advert => {
        if (advert && Object.keys(advert).length) {
          this.advert = { ...advert, type: ADVERT_TYPE.OLX };
        }
      }));
  }

  private initAdvert(): Observable<any> {
    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.OLX) {
      return this.getAdvert(this.chat.id);
    }
    if (RIA_CHAT_TYPES.includes(this.chat.external_type)) {
      this.initRiaAdvert();
    }

    return of(null);
  }

  private initRiaAdvert() {
    if (!this.chat.x_data?.advert) return;

    const riaAdvert: IRiaAdvert = this.chat.x_data.advert;
    this.advert = {
      type: ADVERT_TYPE.RIA,
      id: riaAdvert.id,
      title: riaAdvert.title,
      url: riaAdvert.url,
      images: [{ url: riaAdvert.main_photo_url }],
      double_price: {
        usd: riaAdvert.price_usd,
        uah: riaAdvert.price,
      },
      description: riaAdvert.short_info,
    };
  }

  public doPinnedMessage(message: Message, is_pinned: boolean) {
    return this.http.put<any>(environment.baseURL + `/chats/${message.id}/do-pinned-message`, { is_pinned })
      .pipe(map(res => res.data), tap(data => {
        message.is_pinned = data.is_pinned;
      }));
  }

  public getPinnedByChat(): void {
    this.http.get<any>(environment.baseURL + `/chats/${this.selectedChatId}/pinned-messages`)
      .pipe(map(res => res.data))
      .subscribe({
        next: (res) => {
          this.chatPinnedInfo.items = res || [];
          this.chatPinnedInfo.grouped_items = this.groupMessagesByDate(this.chatPinnedInfo.items);
          if (!this.chatPinnedInfo.items?.length) {
            this.chatPinnedInfo.visible = false;
          }
        },
      });
  }

  getDelayedByChat(): void {
    this.http.get<IResponseWithData<Message[]>>(environment.baseURL + `/chats/${this.selectedChatId}/delayed-messages`)
      .pipe(map(res => res.data))
      .subscribe({
        next: (res) => {
          this.chatDelayedInfo.items = res || [];
          this.chatDelayedInfo.grouped_items = this.groupMessagesByDate(this.chatDelayedInfo.items);
          if (!this.chatDelayedInfo.items?.length) {
            this.chatDelayedInfo.visible = false;
          }
        },
      });
  }

  refreshDelayedMessages(chatId: string): void {
    if (this.chat.id === chatId) {
      this.getDelayedByChat();
    }
  }

  public handleMessageAction(chat_id: string, body: any) {
    return this.http.post<any>(environment.baseURL + `/chats/${chat_id}/message`, body)
      .pipe(map(res => res.data));
  }

  public sendMessageAction(thread_id: string, payload: ISendMessageBody) {
    return this.http.post<any>(environment.baseURL + `/chats/${thread_id}/message`, payload)
      .pipe(map(res => res.data));
  }

  closeChat() {
    this.accessCheckSubscription?.unsubscribe();
    this.selectedChatId = '';
    this.initChatIdQuery('');
  }

  savePositionAndElement(event: any) {
    const container = event.target;
    const containerRect = container.getBoundingClientRect();

    const firstVisibleMessage = this.chatMessagesInfo.items.find((message, index) => {
      const itemElement = document.getElementById(message.id);
      if (!itemElement) return false;
      const itemRect = itemElement.getBoundingClientRect();
      return itemRect.bottom > containerRect.top && itemRect.top < containerRect.bottom;
    });

    if (firstVisibleMessage) {
      const firstVisibleElement = document.getElementById(firstVisibleMessage.id);
      if (firstVisibleElement) {
        const firstElementRect = firstVisibleElement.getBoundingClientRect();

        this.savedPositionData = {
          offset: firstElementRect.top - containerRect.top,
          message_id: firstVisibleMessage.id,
        };
      }
    }
  }

  private resetIfLoadedScroll(event: any) {
    const container = event.target;

    const element = document.getElementById(this.savedPositionData.message_id);

    if (element) {
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;

      container.scrollTop = elementTop - containerTop + container.scrollTop - this.savedPositionData.offset;
    }
  }

  private getMessageSearchList(params: { page: number, take: number, search: string }) {
    return this.http.get<any>(environment.baseURL + `/chats/${this.selectedChatId}/messages/search`, { params })
      .pipe(map(res => res.data));
  }

  public searchChatMessages(pagination?: boolean) {
    this.chatSearchInfo.loading = true;
    const params = {
      page: this.chatSearchInfo.page_number,
      take: 25,
      search: this.chatSearchInfo.search_text,
    };
    this.chatSearchInfo.items_request = this.getMessageSearchList(params).subscribe({
      next: (response: any) => {
        if (pagination && this.chatSearchInfo.items) {
          this.chatSearchInfo.items = [...this.chatSearchInfo.items, ...response.items];
        } else {
          this.chatSearchInfo.items = (response.items || []);
        }

        this.chatSearchInfo.page_count = Math.ceil(
          response.total / response.size,
        );
      },
      error: () => {
        this.chatSearchInfo.loading = false;
      },
      complete: () => {
        this.chatSearchInfo.loading = false;
      },
    });
  }

  public getUserInfo() {
    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.OLX && this.chat.x_data?.interlocutor_id) {
      const url = environment.baseURL + `/olx/users/${this.chat.x_data?.interlocutor_id}`;
      return this.http.get<any>(url).pipe(
        tap(res => {
          if (res.data) {
            this.userInfo = res.data;
          }
          this.initActions();
        }),
      );
    } else if (RIA_CHAT_TYPES.includes(this.chat.external_type)) {
      return of(null).pipe(tap(() => {
        if (this.chat.x_data?.opponent) {
          this.userInfo = this.chat.x_data.opponent;
        }
      }));
    }

    return of(null);
  }

  public initActions() {
    this.aboutActions = [];

    // this.aboutActions.push({
    //   title: 'notifications',
    //   icon: 'notifications',
    //   key: 'notifications',
    //   tool: TOOLS.TOGGLE,
    //   value: true,
    //   action: (item) => this.toggleNotifications(item),
    // });

    if (this.userInfo?.user_ads_url) {
      this.aboutActions.push({
        title: 'olx_profile',
        icon: 'account_circle',
        is_svg: false,
        tool: TOOLS.PROFILE,
        copyValue: this.userInfo?.user_ads_url,
        action: () => window.open(this.userInfo?.user_ads_url),
      });
    }

    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.INSTAGRAM) {
      const username = this.chat.x_data?.username?.trim();
      const phone = this.chat.x_data?.number?.trim();

      if (username) {
        const profileLink = `https://www.instagram.com/${username}/`;
        this.aboutActions.push({
          title: 'instagram_profile',
          icon: 'account_circle',
          tool: TOOLS.BUTTON,
          copyValue: profileLink,
          action: () => window.open(profileLink, '_blank'),
        });
      }

      if (phone) {
        const telLink = `tel:${phone.replace(/\s+/g, '')}`;
        this.aboutActions.push({
          title: 'instagram_phone',
          icon: 'call',
          tool: TOOLS.BUTTON,
          description: phone,
          copyValue: phone,
          action: () => window.open(telLink),
        });
      }
    }

    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.TELEGRAM_USER) {
      const username = this.chat.x_data?.username?.trim();
      const phone = this.chat.x_data?.number?.trim();

      if (username) {
        const profileLink = `https://t.me/${username}`;
        this.aboutActions.push({
          title: 'telegram_profile',
          icon: 'account_circle',
          tool: TOOLS.BUTTON,
          copyValue: profileLink,
          action: () => window.open(profileLink, '_blank'),
        });
      }

      if (phone) {
        const telLink = `tel:${phone.replace(/\s+/g, '')}`;
        this.aboutActions.push({
          title: 'telegram_phone',
          icon: 'call',
          tool: TOOLS.BUTTON,
          description: phone,
          copyValue: phone,
          action: () => window.open(telLink),
        });
      }
    }

    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.VIBER && this.chat.x_data?.number) {
      const phone = this.chat.x_data.number.trim();
      const telLink = `tel:${phone.replace(/\s+/g, '')}`;
      this.aboutActions.push({
        title: 'viber_phone',
        icon: 'call',
        tool: TOOLS.BUTTON,
        description: phone,
        copyValue: phone,
        action: () => window.open(telLink),
      });
    }

    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.WHATSAPP && this.chat.x_data?.number) {
      const phone = this.chat.x_data.number.trim();
      const telLink = `tel:${phone.replace(/\s+/g, '')}`;
      this.aboutActions.push({
        title: 'whatsapp_phone',
        icon: 'call',
        tool: TOOLS.BUTTON,
        description: phone,
        copyValue: phone,
        action: () => window.open(telLink),
      });
    }

    if (this.chat.is_ai_automation) {
      this.aboutActions.push({
        title: 'automation',
        icon: 'robot',
        is_svg: true,
        key: 'automation',
        tool: TOOLS.TOGGLE,
        value: this.chat.settings?.enable_ai_assistant ?? true,
        action: (item) => this.toggleAutomation(item),
      });
    }

    this.aboutActions.push({
      title: this.chat.is_pinned ? 'unpin' : 'pin',
      icon: this.chat.is_pinned ? 'unpin' : 'pin',
      is_svg: true,
      tool: TOOLS.BUTTON,
      action: () => this.onChangePin(),
    });

    if (this.chat.tab_status !== CHAT_TABS.SPAM) {
      if (this.chat.tab_status === CHAT_TABS.ACTIVE) {
        this.aboutActions.push({
          title: 'close_chat',
          icon: 'archive',
          tool: TOOLS.BUTTON,
          action: () => this.onMoveChatAbout(CHAT_TABS.CLOSED),
        });
      } else if (this.chat.tab_status === CHAT_TABS.CLOSED) {
        this.aboutActions.push({
          title: 'open_a_chat',
          icon: 'unarchive',
          tool: TOOLS.BUTTON,
          action: () => this.onMoveChatAbout(CHAT_TABS.ACTIVE),
        });
      }
    }

    if (this.chat.external_type === CHAT_EXTERNAL_TYPE.OLX) {
      this.aboutActions.push({
        title: this.chat.is_blocked ? 'unblock' : 'block',
        icon: this.chat.is_blocked ? 'do_not_disturb_off' : 'do_not_disturb_on',
        tool: TOOLS.BUTTON,
        action: () => this.onBlockChat(),
      });
    }

    if (this.chat.tab_status === CHAT_TABS.SPAM) {
      this.aboutActions.push({
        title: 'not_spam',
        icon: 'move_to_inbox',
        tool: TOOLS.BUTTON,
        action: () => this.onMoveChatAbout(CHAT_TABS.ACTIVE),
      });
    } else {
      this.aboutActions.push({
        title: 'move_to_spam',
        icon: 'report',
        tool: TOOLS.BUTTON,
        action: () => this.onMoveChatAbout(CHAT_TABS.SPAM),
      });
    }
  }

  private onChangePin() {
    this.updateChat(this.chat.id, { is_pinned: !this.chat.is_pinned })
      .subscribe();
  }

  private onBlockChat() {
    if (!this.chat.x_data?.interlocutor_id) return;

    if (this.chat.is_blocked) {
      this.unblockUser(String(this.chat.x_data.interlocutor_id), this.chat.external_type);
    } else {
      this.blockUser(String(this.chat.x_data.interlocutor_id), this.chat.external_type, this.chat.title)
        .subscribe();
    }
  }

  private onMoveChatAbout(tabStatus: CHAT_TABS) {
    this.onMoveChat(this.chat.id, tabStatus);
  }

  private toggleNotifications(item: IAboutAction) {
    console.log(item.value);
  }

  private toggleAutomation(item: IAboutAction) {
    this.updateSettings(this.chat.id, {
      enable_ai_assistant: item.value,
    }).subscribe();
  }

  public onMoveChat(chat_id: string, tab_status: CHAT_TABS) {
    const payload: Record<string, any> = {
      tab_status,
    };

    if ([CHAT_TABS.CLOSED, CHAT_TABS.SPAM].includes(tab_status) && this.baseService.currentCompanyInfo?.settings?.is_chat_unpin) {
      payload['is_pinned'] = false;
    }

    this.updateChat(chat_id, payload)
      .subscribe();
  }

  public getStaticChats(params: any) {
    return this.http.get<any>(environment.baseURL + '/statistics/chats', { params })
      .pipe(map(res => res.data));
  }

  public getOlxCount() {
    return this.http.get<any>(environment.baseURL + '/chats/count')
      .pipe(map(res => res.data));
  }

  public loadCount() {
    this.getOlxCount().subscribe({
      next: (res) => { this.message_count = res; },
      error: (error: HttpErrorResponse) => {
        if (this.isChatAccessDenied(error)) this.message_count = 0;
      },
    });
  }

  public updateSettings(chat_id: string, body: any) {
    const url = environment.baseURL + `/chats/settings/${chat_id}`;
    return this.http.post<any>(url, body)
      .pipe(map(res => res.data), tap(data => {
        this.reloadChatList();
        if (data.chat_id === this.chat?.id) {
          this.chat.settings = { ...data };
        }
      }));
  }

  public closeRightMenu(name?: RIGHT_MENU) {
    this.rightMenu = name || null;
  }

  public closeLeftMenu(name?: LEFT_MENU) {
    this.leftMenu = name || null;
  }

  public actionRightMenu(value: any) {
    if (value === this.rightMenu) {
      this.rightMenu = null;
    } else {
      this.rightMenu = value;
    }
  }

  public actionLeftMenu(value: any) {
    if (value === this.leftMenu) {
      this.leftMenu = null;
    } else {
      this.leftMenu = value;
    }
  }

  public getMessageAction(chat: IChatItem, message: Message) {
    this.insertOrMoveChat(chat, message);

    if (this.chat?.id === chat.id) {
      Object.assign(this.chat, chat);

      if (this.baseService.currentUserInfo.settings.is_message_read && this.chat.is_can_read) {
        this.readChat(this.chat).subscribe();
      }

      const candidateIndex = this.chatMessagesInfo.items.findIndex((item: Message) => item.id === message.id);
      if (candidateIndex !== -1) {
        this.chatMessagesInfo.items[candidateIndex] = message;
        this.chatMessagesInfo.grouped_items = this.groupMessagesByDate(this.chatMessagesInfo.items);
      } else if (this.chatMessagesInfo.items && this.hasMessagesInfoItem(MESSAGES_INFO_POSITION.FIRST)) {
        this.chatMessagesInfo.items.unshift(message);
        this.chatMessagesInfo.total_first = message.id;
        this.chatMessagesInfo.grouped_items = this.groupMessagesByDate(this.chatMessagesInfo.items);
      }
    }
  }

  /** Manager and tag filters of the chat list; `none` among them matches the chats without a manager / tags */
  public matchesManagerAndTagFilters(chat: Pick<IChatItem, 'assigned_user' | 'tags'>): boolean {
    if (this.selectedManagers.length) {
      const managerId = chat.assigned_user?.id;
      const matchesManager = managerId
        ? this.selectedManagers.includes(managerId)
        : this.selectedManagers.includes(CHAT_FILTER_NONE);
      if (!matchesManager) return false;
    }

    // A socket payload may come without the tags, then the tag filter can't tell
    if (this.selectedTags.length && chat.tags) {
      const matchesTags = chat.tags.length
        ? chat.tags.some((tag) => this.selectedTags.includes(tag.id))
        : this.selectedTags.includes(CHAT_FILTER_NONE);
      if (!matchesTags) return false;
    }

    return true;
  }

  private insertOrMoveChat(chat: IChatItem, message: Message) {
    const existingIndex = this.chatsInfo.items.findIndex(c => c.id === chat.id);
    if (existingIndex === -1 && !this.chatsInfo.loaded_pages.includes(1)) {
      return;
    }

    let chatToInsert = chat;
    if (existingIndex !== -1) {
      this.chatsInfo.items[existingIndex] = chat;

      if (!this.chatsInfo.loaded_pages.includes(1)) {
        return;
      }

      chatToInsert = this.chatsInfo.items.splice(existingIndex, 1)[0];
    }

    if (this.selectedTab.external_type) {
      if (chat.external_type !== this.selectedTab.external_type) {
        return;
      }
    }

    if (this.selectedChatTab) {
      if (chat.tab_status !== this.selectedChatTab) {
        return;
      }
    }

    if (this.selectedChannels.length) {
      if (!chat.channels.some(ch => this.selectedChannels.includes(ch.id)))
        return;
    }

    if (!this.matchesManagerAndTagFilters(chat)) return;

    if (!this.matchesConversationType(chat, message, existingIndex !== -1)) return;

    if (this.chatsInfo.search_text) {
      const regex = new RegExp(this.chatsInfo.search_text, 'i');
      const match = regex.test(chat.title);
      if (!match) return;
    }

    const isPinned = chat.is_pinned;
    const unreadCount = chat.unread_count;
    const updatedAt = new Date(chat.updated_at).getTime();

    const insertIndex = this.chatsInfo.items.findIndex(i => {
      if (isPinned && !i.is_pinned) return true;
      if (!isPinned && i.is_pinned) return false;

      if (this.selectedChatSort === CHAT_SORT.IS_UNREAD) {
        if (unreadCount && !i.unread_count) return true;
        if (!unreadCount && i.unread_count) return false;
      }

      const iUpdatedAt = new Date(i.updated_at).getTime();
      return updatedAt > iUpdatedAt;
    });

    const indexToInsert = insertIndex === -1 ? this.chatsInfo.items.length : insertIndex;
    this.chatsInfo.items.splice(indexToInsert, 0, chatToInsert);

    if (existingIndex === -1 && this.chatsInfo.items.length > 75) {
      this.chatsInfo.items.pop();
    }
  }

  /**
   * Mirrors the server `conversation_type` filter for a chat that just got a message.
   * Instagram and Facebook keep comments in `*-COMMENT-` chats until the commenter writes to direct,
   * TikTok keeps comments and direct messages in one chat, so there the new message decides.
   * A listed chat already matched the type on the server, a new message can only take that away in exclusive mode:
   * a message of the opposite kind turns it into a mixed dialog.
   */
  private matchesConversationType(chat: IChatItem, message: Message, isListed: boolean): boolean {
    const conversationType = this.appliedConversationType;
    if (conversationType === CHAT_CONVERSATION_TYPE.ALL) return true;

    const isComments = conversationType === CHAT_CONVERSATION_TYPE.COMMENTS;
    if (!COMMENT_CHAT_EXTERNAL_TYPES.includes(chat.external_type)) return !isComments;

    // Socket payloads may come without the message
    const isCommentMessage =
      message?.type_message === MESSAGE_TYPES.TEXT_COMMENT || isCommentExternalId(message?.external_id);
    if (this.appliedConversationExclusive && message && isComments !== isCommentMessage) return false;
    if (isListed) return true;

    if (chat.external_type === CHAT_EXTERNAL_TYPE.TIKTOK) return isComments === isCommentMessage;

    const isCommentChat = isCommentExternalId(chat.external_id);
    return isComments ? isCommentChat || isCommentMessage : !isCommentChat;
  }

  public addTempMessage(message: Message) {
    if (this.hasMessagesInfoItem(MESSAGES_INFO_POSITION.FIRST)) {
      this.chatMessagesInfo.items.unshift(message);
      this.chatMessagesInfo.total_first = message.id;
      this.chatMessagesInfo.grouped_items = this.groupMessagesByDate(this.chatMessagesInfo.items);
    }
  }

  public waitEditMessage(messageId: string, text: string) {
    const candidate = this.chatMessagesInfo.items.find((v) => v.id === messageId);
    if (candidate) {
      candidate.text = text;
      candidate.status = MESSAGE_STATUS.WAIT;
    }
  }

  public downloadMedia(chat_id: string, media_id: string | number, type?: DOWNLOAD_MEDIA_TYPE) {
    return this.http.post(environment.baseURL + '/chats/media', { chat_id, media_id, type }, { responseType: 'blob' });
  }

  public hasMessagesInfoItem(position: MESSAGES_INFO_POSITION) {
    if (!this.chatMessagesInfo.total_first && !this.chatMessagesInfo.total_last) return true;

    const itemId = position === MESSAGES_INFO_POSITION.FIRST ? this.chatMessagesInfo.total_first : this.chatMessagesInfo.total_last;
    return this.chatMessagesInfo.items?.some((v) => v.id === itemId);
  }

  public messageUpdatedSocket(updatedMessageData: TMessageUpdatedSocket) {
    switch (updatedMessageData.action) {
      case MessageSocketAction.SEEN: {
        this.messageIsSeen(updatedMessageData?.chat_id);
        break;
      }

      case MessageSocketAction.REACTION: {
        this.addedReactionToMessage(updatedMessageData);
        break;
      }

      case MessageSocketAction.DELETED: {
        this.messageDeleted(updatedMessageData);
        break;
      }

      case MessageSocketAction.TRANSCRIPTION: {
        if (this.chat.id !== updatedMessageData.chat_id || !updatedMessageData.message_id) return;
        this.patchMessageXData(updatedMessageData.message_id, (x_data) => ({
          ...x_data,
          transcription: updatedMessageData.x_data?.transcription,
        }));
        break;
      }

      case MessageSocketAction.TRANSLATION: {
        const { language, translation } = updatedMessageData.x_data ?? {};
        if (this.chat.id !== updatedMessageData.chat_id || !updatedMessageData.message_id || !language) return;
        this.patchMessageXData(updatedMessageData.message_id, (x_data) => ({
          ...x_data,
          translations: { ...(x_data.translations ?? {}), [language]: translation },
        }));
        break;
      }

    }
  }

  private messageIsSeen(chat_id: string) {
    try {
      if (this.chat.id !== chat_id) return;

      this.markMessagesAsSeen(this.chatMessagesInfo.grouped_items);
      if (this.chatPinnedInfo.grouped_items) {
        this.markMessagesAsSeen(this.chatPinnedInfo.grouped_items);
      }
    } catch (e) {
    }
  }

  private addedReactionToMessage(updatedMessageData: TMessageUpdatedSocket) {
    try {
      if (this.chat.id !== updatedMessageData.chat_id) return;

      this.findAndAddReaction(this.chatMessagesInfo, updatedMessageData);
      if (this.chatPinnedInfo.items?.length) {
        this.findAndAddReaction(this.chatPinnedInfo, updatedMessageData);
      }

    } catch (e) {
    }
  }

  private messageDeleted(updatedMessageData: TMessageUpdatedSocket) {
    if (this.chat.id !== updatedMessageData.chat_id) return;

    this.findAndMarkDeleted(this.chatMessagesInfo.grouped_items, updatedMessageData);
    if (this.chatPinnedInfo.grouped_items) {
      this.findAndMarkDeleted(this.chatPinnedInfo.grouped_items, updatedMessageData);
    }
  }

  private markMessagesAsSeen(groupedItems: TGroupItem[]) {
    for (const group of groupedItems) {
      if (!group?.messages) continue;

      for (const message of group.messages) {
        if (!message.is_read && message.status === MESSAGE_STATUS.SEND) {
          message.is_read = true;
        }
      }
    }
  }

  private findAndAddReaction(messageInfo: IChatMessagesInfo | IChatAdditionalInfo, updatedMessageData: TMessageUpdatedSocket) {
    const candidateIndex = messageInfo.items.findIndex((item: Message) => item.id === updatedMessageData.message_id);

    if (candidateIndex !== -1) {
      const message = messageInfo.items[candidateIndex];
      messageInfo.items[candidateIndex] = {
        ...message,
        x_data: {
          ...(message.x_data ?? {}),
          reaction: updatedMessageData?.x_data?.reaction,
        },
      };
      messageInfo.grouped_items = this.groupMessagesByDate(messageInfo.items);
    }
  }

  /** Replaces the message with a patched `x_data` copy so `ChatMessageComponent` re-inits via its input setter. */
  public patchMessageXData(message_id: string, patch: (x_data: NonNullable<Message['x_data']>) => Message['x_data']) {
    for (const messageInfo of [this.chatMessagesInfo, this.chatPinnedInfo]) {
      const index = messageInfo.items?.findIndex((item: Message) => item.id === message_id) ?? -1;
      if (index === -1) continue;

      const message = messageInfo.items[index];
      messageInfo.items[index] = { ...message, x_data: patch(message.x_data ?? {}) };
      messageInfo.grouped_items = this.groupMessagesByDate(messageInfo.items);
    }
  }

  public transcribeMessage(message_id: string, force = false) {
    return this.http
      .post<IResponseWithData<MessageTranscription>>(
        environment.baseURL + `/message-ai/${message_id}/transcribe`,
        {},
        { params: force ? { force } : {} },
      )
      .pipe(map((res) => res.data));
  }

  public translateMessage(message_id: string, language: string, force = false) {
    return this.http
      .post<IResponseWithData<TranslateMessageResponse>>(environment.baseURL + `/message-ai/${message_id}/translate`, {
        language,
        force,
      })
      .pipe(map((res) => res.data));
  }

  private findAndMarkDeleted(groupedItems: TGroupItem[], updatedMessageData: TMessageUpdatedSocket) {
    for (const group of groupedItems) {
      const message = group.messages.find(m => m.id === updatedMessageData.message_id);
      if (message) {
        message.deleted_at = updatedMessageData?.x_data?.deletedAt;
        return;
      }
    }
  }

  public deleteMessage(message_id: string) {
    return this.http.delete<any>(environment.baseURL + `/admin/messages/${message_id}`)
      .pipe(tap(() => {
        this.removeMessageLocally(message_id);
        this.reloadChatList();
      }));
  }

  private removeMessageLocally(message_id: string) {
    this.chatMessagesInfo.items = this.chatMessagesInfo.items.filter((item: Message) => item.id !== message_id);
    this.chatMessagesInfo.grouped_items = this.groupMessagesByDate(this.chatMessagesInfo.items);

    if (this.chatPinnedInfo.items?.length) {
      this.chatPinnedInfo.items = this.chatPinnedInfo.items.filter((item: Message) => item.id !== message_id);
      this.chatPinnedInfo.grouped_items = this.groupMessagesByDate(this.chatPinnedInfo.items);
    }
  }

  public sendMessageReaction(requestBody: any) {
    return this.http.post<any>(environment.baseURL + '/chats/message-reaction', requestBody)
      .pipe((map(res => res.data)));
  }

  public createChatForMessage(requestBody: CreateChatForMessagePayload) {
    return this.http.post<IResponseWithData<string>>(
      environment.baseURL + '/chats/create-chat-for-message',
      requestBody,
    ).pipe(map(res => res.data));
  }

  public canEditMessage(message: Message) {
    const isMessageSent = message.status === MESSAGE_STATUS.SEND;
    const isMessageByOwner = !!message.created_by;
    const isMessageNotForwarded = !message.x_data?.fwd_from;
    const isMessageNotDeleted = !message.deleted_at;
    const isMessageHasText = !!message.text;
    const isMessageHasNotEditableAttachments = !!message.attachments?.array?.some((v) => !noEditAttachmentTypes.includes(v.type));

    return isMessageSent && isMessageByOwner && isMessageNotForwarded && isMessageNotDeleted
      && (isMessageHasText || isMessageHasNotEditableAttachments);
  }

  public canReplyToMessage(message: Message) {
    const isMessageSent = message.status === MESSAGE_STATUS.SEND;
    const isMessageNotDeleted = !message.deleted_at;

    return isMessageSent && isMessageNotDeleted;
  }
}
